<template>
  <div class="max-w-2xl">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-blue">What School District do I live in?</h1>
    <div space-y-10 mt-4>
      <LocationLookup v-model="selectedLocation" />
      <div v-if="loading || !data.schoolDistrict" :class="{ invisible: !loading }" space-y-6>
        <div space-y-3>
          <p h-5 w-30 bg-stone-100 rounded animate-pulse></p>
          <p h-9 w-70 bg-stone-100 rounded animate-pulse></p>
        </div>
        <SchoolDistrictDetailsPlaceholder />
      </div>
      <div space-y-10 v-if="data.schoolDistrict">
        <div v-for="district in data.schoolDistrict" :key="district.id">
          <div space-y-6>
            <div>
              <p font-semibold text-stone-500 text-lg>{{ district.lowGrade }} to {{ district.highGrade }}</p>
              <h2 font-bold text-3xl sm:text-4xl>{{ district.name }}</h2>
            </div>
            <SchoolDistrictDetails :district="district" :loading="loading" />
          </div>
        </div>
      </div>
      <div v-else-if="loading">
        <!-- TODO: add loading placeholder -->
      </div>
      <div space-y-4>
        <Ad ad-slot="1948746426" />
        <h2 text-2xl font-semibold text-stone-500>School District Boundary Map</h2>
        <LazyMapSchoolDistrict
          :location="addressLocation"
          :schoolDistrict="data.schoolDistrict"
          @markerMove="onMarkerMove"
        />
        <Ad ad-slot="3658187561" />
        <h2 text-2xl font-semibold text-stone-500>Your Location</h2>
        <div grid grid-flow-dense sm:grid-cols-2 gap-2>
          <div v-for="item in SCHOOL_DISTRICT_FIELDS" contents>
            <DataItem
              v-if="!['nativeLands', 'countySubdivision.fullName'].includes(item.field)"
              :loading="loading"
              :label="item.label"
              :value="getDataValue(item.field)"
              valueClass="text-xl sm:text-2xl"
              col-span-2
              sm:col-span-1
              :class="item.class"
            />
            <CountySubdivisionDataItem
              v-else-if="item.field == 'countySubdivision.fullName'"
              :loading="loading"
              :data="data.countySubdivision"
            />
          </div>
        </div>
      </div>
      <div text-stone-5 prose v-if="isLocationSupported">
        <p v-if="!loading && address?.error && !isBot()">An error occurred. Please try again later.</p>
        <p v-else-if="!loading && !data.schoolDistrict && !isBot()">
          We could not determine the School District for your location.
        </p>
        <p>
          By default, the site will gather the gps coordinates directly from your device and then uses that information
          to lookup information about where you are.
        </p>
      </div>
    </div>
    <WikipediaInfo py-10 v-if="selectedLocation?.name" :slug="selectedLocation.name" />
    <div class="prose">
      <ContentRenderer v-if="page" :value="page" />
    </div>
  </div>
</template>
<script setup>
const router = useRouter();
const route = useRoute();

const page = await useContent("/school-district");

useSeoMeta({
  title: () => {
    if (route.query.title) {
      return `${decodeURI(route.query.title)} - ${page.value.title}`;
    }
    return `${page.value.title} - ${page.value.subtitle}`;
  },
});

const {
  location,
  updateLocation,
  startWatching,
  stopWatching,
  loading: locationLoading,
  isLocationSupported,
} = useLocation();
const selectedLocation = ref({
  name: "Current Location",
  module: "currentLocation",
});
const address = ref();
const countySubdivision = ref();
const schoolDistrict = ref();

const loading = ref(true);
const watchMode = ref(false);

updateLocation();

const addressLocation = computed(() => {
  if (selectedLocation.value?.module === "currentLocation") {
    if (!location.value?.latitude) {
      return null;
    }
    return {
      latitude: location.value.latitude,
      longitude: location.value.longitude,
    };
  }
  if (selectedLocation.value?.location) {
    return selectedLocation.value.location;
  }
  return null;
});

watch(selectedLocation, () => {
  if (selectedLocation.value == null) {
    return;
  }
  loading.value = true;
  address.value = null;
  countySubdivision.value = null;
  schoolDistrict.value = null;
});

function onMarkerMove(event) {
  selectedLocation.value = {
    name: "Map Location",
    module: "mapLocation",
    location: {
      latitude: event.lat,
      longitude: event.lng,
    },
  };
}

function getDataValue(field) {
  const path = field.split(".");
  let value = data.value;
  for (const key of path) {
    value = value?.[key];
  }
  return value;
}

const data = computed(() => {
  return {
    ...address.value?.data,
    countySubdivision: countySubdivision.value?.data,
    schoolDistrict: schoolDistrict.value?.data,
  };
});

watchEffect(() => {
  watchMode.value ? startWatching() : stopWatching();
});
watch(addressLocation, async () => {
  if (!isNumber(addressLocation.value?.latitude)) {
    return;
  }
  try {
    // rewritten within Promise.all():
    const [addressResponse, countySubdivisionResponse, schoolDistrictResponse] = await Promise.all([
      useFetchLocation(addressLocation.value),
      useFetchCountySubdivision(addressLocation.value),
      useFetchSchoolDistrict(addressLocation.value),
    ]);
    schoolDistrict.value = await schoolDistrictResponse;
    loading.value = false;
    address.value = addressResponse;
    countySubdivision.value = countySubdivisionResponse;
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
});

// run on load
function setLocationFromRoute() {
  if (!route.query.lng) {
    // default to current location
    selectedLocation.value = {
      name: "Current Location",
      module: "currentLocation",
    };
    if (!isLocationSupported.value) {
      selectedLocation.value = null;
    }
  } else {
    selectedLocation.value = {
      name: route.query.title ? decodeURI(route.query.title) : "Saved Location",
      module: route.query.title ? "urlLocation" : "savedLocation",
      location: {
        latitude: parseFloat(route.query.lat),
        longitude: parseFloat(route.query.lng),
      },
    };
  }
}
setLocationFromRoute();

// user selected a new location
watch(selectedLocation, async () => {
  if (selectedLocation.value == null) {
    return;
  }

  if (selectedLocation.value?.module === "currentLocation") {
    // request permission to access location
    await updateLocation();
    if (!isLocationSupported.value) {
      selectedLocation.value = null;
      enableLocationDisabledDialog();
      return;
    }
  }

  // update page url with new location
  const query = {};
  if (isNumber(addressLocation.value?.latitude)) {
    query.lat = round(addressLocation.value.latitude, 5);
    query.lng = round(addressLocation.value.longitude, 5);
  }
  if (!["currentLocation", "savedLocation"].includes(selectedLocation.value?.module)) {
    query.title = encodeURI(selectedLocation.value.name);
  }
  router.push({ query });
});
</script>
